<template>
    <div class="row">
        <div class="col-3 d-flex">
            <div class="box">
                <div class="box-body">
                    <template v-if="pages">
                        <h2>Search</h2>
                        <table>
                            <tr>
                                <td>
                                    <input v-model="searchString"
                                           type="text"
                                           @keyup="searchChanged">
                                    <i class="icon icon-wrong icon-in-input"
                                       @click="clearSearchClicked" />
                                </td>
                                <td class="fit-to-content">
                                    <button type="button"
                                            class="btn outline icon-button"
                                            @click="searchClicked">
                                        <i class="icon icon-magnify" />
                                    </button>
                                </td>
                            </tr>
                        </table>
                        <br>
                        <h2>Contents</h2>
                        <p v-for="(page, index) in pages"
                           :key="`title-${index}`">
                            <a class="link-tem"
                               @click="setCurrentPage(index)">
                                <template v-if="index == currentPageNumber">
                                    <b>
                                        {{ page.name1 }}
                                    </b>
                                </template>
                                <template v-else>
                                    {{ page.name1 }}
                                </template>
                            </a>
                        </p>
                    </template>
                </div>
            </div>
        </div>
        <div class="col-9 d-flex">
            <div class="box help-box">
                <div class="box-body">
                    <template v-if="pageLoaded">
                        <!-- eslint-disable vue/no-v-html -->
                        <div v-html="currentPage" />
                        <!-- eslint-enable -->
                    </template>
                </div>
                <div class="box-footer">
                    <div>
                        <span class="float-left">
                            <button type="button"
                                    class="btn btn-primary btn-sm btn-dark-color"
                                    :disabled="atStart"
                                    @click="previousClicked">
                                Back
                            </button>
                        </span>
                        <span class="float-right">
                            <button type="button"
                                    class="btn btn-primary btn-sm btn-dark-color"
                                    :disabled="atEnd"
                                    @click="nextClicked">
                                Next
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup>
    import { ref, defineProps, computed, onMounted, nextTick } from 'vue';
    import axios from 'axios';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'

    const props = defineProps({
        displayed: {
            type: String,
            required: true
        },
        intialHelpFile: {
            type: String,
            default: ''
        }
    });

    const pageLoaded = computed(() => !!currentPage.value);
    const lastPageNumber = computed(() => pages.value.length - 1);
    const atStart = computed(() => currentPageNumber.value <= 0);
    const atEnd = computed(() => currentPageNumber.value >= lastPageNumber.value);

    const pages = ref([]);
    const currentPageNumber = ref(0);
    const currentPage = ref(null);
    const searchString = ref("");

    onMounted(() => {
        loadPages();
        scrollToTopWithBehaviour("instant");

    });

    function loadPages() {
        let data = {
            value: searchString.value
        };

        axios
            .post("/Help/GetHelpPages", data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    pages.value = response.data.helpPages;
                    showInitialPage();
                } else {
                    alert(`Failed to read help pages: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Get help pages", axios, error);
            });
    }

    function showInitialPage() {
        currentPageNumber.value = getInitialPageValue();

        if (pages.value.length > 0) {
            let pageName = getCurrentPage();
            loadPage(pageName);
        } else {
            currentPage.value = null;
        }
    }

    function getInitialPageValue() {
        if (props.intialHelpFile) {
            let pos = pages.value.findIndex((p) => p.name2 == props.intialHelpFile);
            return (pos >= 0) ? pos : 0;
        } else {
            return 0;
        }
    }

    function getCurrentPage() {
        return pages.value[currentPageNumber.value].name2;
    }

    function loadCurrentPage() {
        let pageName = getCurrentPage();
        let encodedName = encodeURI(pageName);
        window.history.replaceState(null, null, `/GetHelp?displayed=${props.displayed}&helpFile=${encodedName}`);

        loadPage(pageName);
    }

    function loadPage(pageName) {
        let data = {
            value: pageName
        };

        axios
            .post("/Help/GetHelpPage", data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    currentPage.value = response.data.helpPage;

                    scrollToTop();

                } else {
                    alert(`Failed to read help page: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Get help page", axios, error);
            });
    }

    function setCurrentPage(pageNumber) {
        if ((pageNumber >= 0) && (pageNumber <= lastPageNumber.value)) {
            currentPageNumber.value = pageNumber;
            loadCurrentPage();
        }
    }

    function previousClicked() {
        if (!atStart.value) {
            --currentPageNumber.value;
            loadCurrentPage();
        }
    }

    function nextClicked() {
        if (!atEnd.value) {
            ++currentPageNumber.value;
            loadCurrentPage();
        }
    }

    function searchClicked() {
        loadPages();
    }

    function clearSearchClicked() {
        searchString.value = "";
        loadPages();
    }

    function searchChanged(event) {
        if (event.key == "Enter") {
            searchClicked();
        } else if (!searchString.value || searchString.value.length > 2) {
            loadPages();
        }
    }

    function scrollToTop() {
        scrollToTopWithBehaviour("instant");
    }

    function scrollToTopWithBehaviour(behavior) {
        nextTick(() => {
            let content = document.getElementById("content");
            content.scrollIntoView({ block: "start", behavior });
        });

    }  

</script>

<style>
</style>

